import React, { useState, useEffect, useCallback, useRef } from 'react';
import CONFIG from '../config';
import '../styles/components/park-videos-sidebar.css';

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
const DESKTOP_MIN_WIDTH_PX = 769;
const DESKTOP_MEDIA_QUERY = `(min-width: ${DESKTOP_MIN_WIDTH_PX}px)`;

/**
 * Indique si le viewport courant est assez large pour afficher la sidebar YouTube.
 */
const getIsDesktopViewport = () => {
  if (typeof window === 'undefined') return false;
  if (typeof window.matchMedia === 'function') {
    return window.matchMedia(DESKTOP_MEDIA_QUERY).matches;
  }
  return (window.innerWidth || 0) >= DESKTOP_MIN_WIDTH_PX;
};

/**
 * Affiche le bouton et le panneau de vidéos YouTube d'un parc sur desktop uniquement.
 */
const ParkVideosSidebar = ({ parkSlug, lang }) => {
  const [isDesktopViewport, setIsDesktopViewport] = useState(getIsDesktopViewport);
  const [hasMinVisits, setHasMinVisits] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isButtonVisible, setIsButtonVisible] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [videos, setVideos] = useState([]);
  const [parkName, setParkName] = useState(null);
  const [errorType, setErrorType] = useState(null); // 'none' | 'noVideos' | 'unavailable'
  const sidebarRef = useRef(null);
  const [buttonTopPercent, setButtonTopPercent] = useState(() => {
    if (typeof window === 'undefined') return 50;
    try {
      const saved = window.localStorage.getItem('yt_sidebar_button_top');
      const value = saved ? parseFloat(saved) : 50;
      if (Number.isNaN(value)) return 50;
      return Math.min(90, Math.max(10, value));
    } catch {
      return 50;
    }
  });
  const isDraggingRef = useRef(false);
  const moveHandlerRef = useRef(null);
  const endHandlerRef = useRef(null);
  const dragStartYRef = useRef(0);
  const dragStartTopRef = useRef(50);
  const isMountedRef = useRef(true);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    if (typeof window.matchMedia !== 'function') {
      setIsDesktopViewport((window.innerWidth || 0) >= DESKTOP_MIN_WIDTH_PX);
      return undefined;
    }

    const mediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);
    const handleViewportChange = (event) => {
      setIsDesktopViewport(event.matches);
    };

    setIsDesktopViewport(mediaQuery.matches);

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleViewportChange);
      return () => mediaQuery.removeEventListener('change', handleViewportChange);
    }

    mediaQuery.addListener(handleViewportChange);
    return () => mediaQuery.removeListener(handleViewportChange);
  }, []);

  // Compteur de visites global du site (une fois par session React)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const rawVisits = window.localStorage.getItem('site_visits');
      const visits = rawVisits ? parseInt(rawVisits, 10) || 0 : 0;
      const newVisits = visits + 1;
      window.localStorage.setItem('site_visits', String(newVisits));
      setHasMinVisits(newVisits >= 4);
    } catch {
      setHasMinVisits(false);
    }
  }, []);

  // Lecture de l'état de rejet (30 jours)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const raw = window.localStorage.getItem('yt_sidebar_dismissed');
      if (!raw) return;
      const ts = parseInt(raw, 10);
      if (!Number.isNaN(ts) && Date.now() - ts < THIRTY_DAYS_MS) {
        setIsDismissed(true);
      }
    } catch {
      setIsDismissed(false);
    }
  }, []);

  // Déclenchement de l'animation d'apparition du bouton
  const shouldRender = isDesktopViewport && !!parkSlug && hasMinVisits && !isDismissed;

  useEffect(() => {
    if (!shouldRender) return undefined;

    const timer = window.setTimeout(() => {
      setIsButtonVisible(true);
    }, 800);

    return () => window.clearTimeout(timer);
  }, [shouldRender]);

  // Gestion des clics en dehors pour fermer la sidebar
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDrawerOpen && sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setIsDrawerOpen(false);
      }
    };

    if (isDrawerOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isDrawerOpen]);

  // Changement de parc ou de langue : réinitialiser les données vidéos
  useEffect(() => {
    setHasFetched(false);
    setVideos([]);
    setErrorType(null);
    setParkName(null);
    setIsDrawerOpen(false);
  }, [parkSlug, lang]);

  const updateButtonPosition = useCallback((clientY) => {
    if (typeof window === 'undefined' || !isMountedRef.current) return;
    const viewportHeight = window.innerHeight || 1;
    const rawPercent = (clientY / viewportHeight) * 100;
    const clamped = Math.min(90, Math.max(10, rawPercent));
    setButtonTopPercent(clamped);
  }, []);

  const detachDragListeners = useCallback(() => {
    if (typeof window === 'undefined') return;

    const handleMove = moveHandlerRef.current;
    const handleEnd = endHandlerRef.current;

    if (!handleMove || !handleEnd) return;

    window.removeEventListener('mousemove', handleMove);
    window.removeEventListener('touchmove', handleMove);
    window.removeEventListener('mouseup', handleEnd);
    window.removeEventListener('touchend', handleEnd);
    window.removeEventListener('touchcancel', handleEnd);

    moveHandlerRef.current = null;
    endHandlerRef.current = null;
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      detachDragListeners();
    };
  }, [detachDragListeners]);

  useEffect(() => {
    if (isDesktopViewport) return;
    setIsDrawerOpen(false);
    setIsButtonVisible(false);
    detachDragListeners();
  }, [detachDragListeners, isDesktopViewport]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem('yt_sidebar_button_top', String(buttonTopPercent));
    } catch {
      // ignorer
    }
  }, [buttonTopPercent]);

  const attachDragListeners = useCallback(() => {
    if (typeof window === 'undefined') return;
    if (!isMountedRef.current) return;

    detachDragListeners();

    const handleMove = (event) => {
      if (!isDraggingRef.current) return;

      // Empêcher le scroll de la page pendant le drag (uniquement pour les événements tactiles)
      if (event.touches && event.touches.length > 0) {
        if (event.cancelable) {
          event.preventDefault();
        }
        updateButtonPosition(event.touches[0].clientY);
      } else if (typeof event.clientY === 'number') {
        updateButtonPosition(event.clientY);
      }
    };

    const handleEnd = () => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      detachDragListeners();
    };

    moveHandlerRef.current = handleMove;
    endHandlerRef.current = handleEnd;

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('touchmove', handleMove, { passive: false });
    window.addEventListener('mouseup', handleEnd);
    window.addEventListener('touchend', handleEnd);
    window.addEventListener('touchcancel', handleEnd);
  }, [detachDragListeners, updateButtonPosition]);

  const fetchVideos = useCallback(async () => {
    if (!parkSlug || hasFetched || loading) return;
    try {
      setLoading(true);
      setErrorType(null);

      // Utiliser l’URL backend documentée pour les vidéos parc
      const baseUrl = CONFIG.API_URL || '';
      const searchParams = new URLSearchParams();
      searchParams.set('park_id', parkSlug);
      if (lang) {
        searchParams.set('lang', lang);
      }
      const url = `${baseUrl}/api/park-videos?${searchParams.toString()}`;
      const response = await fetch(url);
      let data = null;
      try {
        data = await response.json();
      } catch {
        data = null;
      }

      if (!response.ok) {
        const code = data && data.code;
        if (code === 'MISSING_PARK_ID' || code === 'PARK_NOT_CONFIGURED') {
          setErrorType('noVideos');
        } else {
          setErrorType('unavailable');
        }
        setVideos([]);
        setParkName(data && data.park ? data.park : null);
        return;
      }

      const videosList = Array.isArray(data && data.videos) ? data.videos.slice(0, 8) : [];
      if (!videosList.length) {
        setErrorType('noVideos');
      }
      setVideos(videosList);
      setParkName(data && data.park ? data.park : null);
      setHasFetched(true);
    } catch {
      setErrorType('unavailable');
      setVideos([]);
    } finally {
      setLoading(false);
    }
  }, [parkSlug, hasFetched, loading, lang]);

  const handleToggleDrawer = () => {
    if (!shouldRender) return;

    const willOpen = !isDrawerOpen;
    setIsDrawerOpen(willOpen);

    if (willOpen && !hasFetched && !loading) {
      fetchVideos();
    }
  };

  const handleDragStart = (event) => {
    // Empêcher l'ouverture/fermeture si l'utilisateur commence un drag franc
    isDraggingRef.current = true;
    attachDragListeners();
    if (event.touches && event.touches.length > 0) {
      dragStartYRef.current = event.touches[0].clientY;
    } else if (typeof event.clientY === 'number') {
      dragStartYRef.current = event.clientY;
    }
    dragStartTopRef.current = buttonTopPercent;
  };

  const handleButtonClick = (event) => {
    // Si on vient de drag, on ignore le click pour éviter les ouvertures involontaires
    if (isDraggingRef.current) {
      event.preventDefault();
      event.stopPropagation();
      isDraggingRef.current = false;
      detachDragListeners();
      return;
    }
    handleToggleDrawer();
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    setIsDrawerOpen(false);
    setIsButtonVisible(false);

    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem('yt_sidebar_dismissed', String(Date.now()));
      } catch {
        // Ignorer les erreurs de stockage
      }
    }
  };

  if (!shouldRender) {
    return null;
  }

  return (
    <div ref={sidebarRef} className="park-videos-sidebar-root" aria-hidden={false}>
      <button
        type="button"
        className={`park-videos-toggle ${isButtonVisible ? 'visible' : ''} ${isDrawerOpen ? 'active' : ''}`}
        style={{ top: `${buttonTopPercent}%` }}
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
        onClick={handleButtonClick}
        aria-label="Vidéos YouTube du parc"
      >
        <span className="yt-toggle-label">YouTube</span>
      </button>

      <div className={`park-videos-drawer ${isDrawerOpen ? 'open' : ''}`}>
        <div className="park-videos-header">
          <div className="park-videos-title">
            <span className="yt-dot" />
            <span>Vidéos YouTube</span>
            {parkName && <span className="park-videos-park-name">{parkName}</span>}
          </div>
          <button
            type="button"
            className="park-videos-close"
            onClick={handleToggleDrawer}
            aria-label="Fermer le panneau vidéos"
          >
            ×
          </button>
        </div>

        <div className="park-videos-content">
          {loading && (
            <div className="park-videos-loader">
              <div className="loader-bar" />
              <div className="loader-bar" />
              <div className="loader-bar" />
            </div>
          )}

          {!loading && errorType === 'unavailable' && (
            <div className="park-videos-message">
              Vidéos indisponibles pour le moment.
            </div>
          )}

          {!loading && errorType === 'noVideos' && (
            <div className="park-videos-message">
              Aucune vidéo disponible pour ce parc pour le moment.
            </div>
          )}

          {!loading && !errorType && videos.length === 0 && (
            <div className="park-videos-message">
              Aucune vidéo disponible pour ce parc pour le moment.
            </div>
          )}

          {!loading && videos.length > 0 && (
            <div className="park-videos-list">
              {videos.map((video, index) => (
                <a
                  key={video.video_url || index}
                  href={video.video_url}
                  className="park-video-item"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <div className="park-video-thumbnail">
                    {video.thumbnail_url && (
                      <img
                        src={`${CONFIG.API_URL || ''}${video.thumbnail_url}`}
                        alt={video.title || 'Vidéo YouTube'}
                        loading="lazy"
                      />
                    )}
                    <span className="park-video-play-icon">▶</span>
                  </div>
                  <div className="park-video-info">
                    {video.title && (
                      <div className="park-video-title">{video.title}</div>
                    )}
                    {video.author && (
                      <div className="park-video-author">{video.author}</div>
                    )}
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>

        <div className="park-videos-footer">
          <button
            type="button"
            className="park-videos-dismiss"
            onClick={handleDismiss}
          >
            Ne plus me montrer ça pendant 30 jours
          </button>
        </div>
      </div>
    </div>
  );
};

export default ParkVideosSidebar;
